import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../services/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Plus } from "lucide-react";

import WorkflowFiltersCard from "../components/approval/WorkflowFiltersCard";
import WorkflowsListCard from "../components/approval/WorkflowsListCard";
import WorkflowFormDialog from "../components/approval/WorkflowFormDialog";
import WorkflowDeleteDialog from "../components/approval/WorkflowDeleteDialog";

const DEFAULT_STEP_FORM = {
  step_order: 1,
  step_name: "",
  description: "",
  approver_role: "SUPERVISOR",
  approver_user_id: null,
  approver_department_id: null,
  is_mandatory: true,
  can_skip: false,
  can_delegate: false,
  requires_comment: false,
  timeout_days: null,
  auto_approve_after_timeout: false,
  auto_reject_after_timeout: false,
  send_reminder_before_timeout: true,
  reminder_days_before: 1,
  notify_approver: true,
  notify_requester_on_approval: true,
  notify_next_step_on_approval: true,
};

const DEFAULT_FORM = {
  name: "",
  code: "",
  description: "",
  is_default: false,
  is_active: true,
  applies_to_absence_reasons: [],
  applies_to_departments: [],
  applies_to_sites: [],
  applies_to_roles: [],
  require_all_steps: true,
  allow_parallel_approval: false,
  notify_on_submission: true,
  notify_on_approval: true,
  notify_on_rejection: true,
  steps: [],
};

const ApprovalWorkflows = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActive, setFilterActive] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [deletingWorkflowId, setDeletingWorkflowId] = useState(null);

  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [stepFormData, setStepFormData] = useState(DEFAULT_STEP_FORM);

  const workflowsQuery = useQuery({
    queryKey: ["approval-workflows"],
    queryFn: () => api.get("v1/approval-workflows", { params: { limit: 1000 } }).then((r) => r.data.items || []),
  });

  useEffect(() => {
    if (workflowsQuery.isError) toast.error(t("approvalWorkflows.loadError"));
  }, [workflowsQuery.isError]);

  const workflows = workflowsQuery.data ?? [];
  const loading = workflowsQuery.isPending;

  const handleOpenDialog = (workflow = null) => {
    if (workflow) {
      setEditingWorkflow(workflow);
      setFormData({
        name: workflow.name,
        code: workflow.code,
        description: workflow.description || "",
        is_default: workflow.is_default,
        is_active: workflow.is_active,
        applies_to_absence_reasons: workflow.applies_to_absence_reasons || [],
        applies_to_departments: workflow.applies_to_departments || [],
        applies_to_sites: workflow.applies_to_sites || [],
        applies_to_roles: workflow.applies_to_roles || [],
        require_all_steps: workflow.require_all_steps,
        allow_parallel_approval: workflow.allow_parallel_approval,
        notify_on_submission: workflow.notify_on_submission,
        notify_on_approval: workflow.notify_on_approval,
        notify_on_rejection: workflow.notify_on_rejection,
        steps: workflow.steps || [],
      });
    } else {
      setEditingWorkflow(null);
      setFormData(DEFAULT_FORM);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingWorkflow(null);
    setEditingStepIndex(null);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleStepInputChange = (field, value) => {
    setStepFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddStep = () => {
    const newStep = { ...stepFormData, step_order: formData.steps.length + 1 };
    setFormData((prev) => ({ ...prev, steps: [...prev.steps, newStep] }));
    setStepFormData({ ...DEFAULT_STEP_FORM, step_order: formData.steps.length + 2 });
    toast.success(t("approvalWorkflows.stepAdded"));
  };

  const handleEditStep = (index) => {
    setStepFormData(formData.steps[index]);
    setEditingStepIndex(index);
  };

  const handleUpdateStep = () => {
    const updatedSteps = [...formData.steps];
    updatedSteps[editingStepIndex] = stepFormData;
    setFormData((prev) => ({ ...prev, steps: updatedSteps }));
    setEditingStepIndex(null);
    setStepFormData({ ...DEFAULT_STEP_FORM, step_order: formData.steps.length + 1 });
    toast.success(t("approvalWorkflows.stepUpdated"));
  };

  const handleCancelStepEdit = () => {
    setEditingStepIndex(null);
    setStepFormData({ ...DEFAULT_STEP_FORM, step_order: formData.steps.length + 1 });
  };

  const handleDeleteStep = (index) => {
    const reorderedSteps = formData.steps
      .filter((_, i) => i !== index)
      .map((step, i) => ({ ...step, step_order: i + 1 }));
    setFormData((prev) => ({ ...prev, steps: reorderedSteps }));
    toast.success(t("approvalWorkflows.stepDeleted"));
  };

  const handleMoveStep = (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.steps.length) return;
    const updatedSteps = [...formData.steps];
    [updatedSteps[index], updatedSteps[newIndex]] = [updatedSteps[newIndex], updatedSteps[index]];
    const reorderedSteps = updatedSteps.map((step, i) => ({ ...step, step_order: i + 1 }));
    setFormData((prev) => ({ ...prev, steps: reorderedSteps }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      toast.error(t("approvalWorkflows.nameCodeRequired"));
      return;
    }
    if (formData.steps.length === 0) {
      toast.error(t("approvalWorkflows.stepRequired"));
      return;
    }
    try {
      if (editingWorkflow) {
        await api.put(`v1/approval-workflows/${editingWorkflow.id}`, formData);
        toast.success(t("approvalWorkflows.updated"));
      } else {
        await api.post("v1/approval-workflows", formData);
        toast.success(t("approvalWorkflows.created"));
      }
      handleCloseDialog();
      queryClient.invalidateQueries({ queryKey: ["approval-workflows"] });
    } catch (error) {
      toast.error(error.response?.data?.detail || t("approvalWorkflows.saveError"));
    }
  };

  const handleToggleStatus = async (workflowId) => {
    try {
      await api.post(`v1/approval-workflows/${workflowId}/toggle-status`);
      toast.success(t("approvalWorkflows.statusToggled"));
      queryClient.invalidateQueries({ queryKey: ["approval-workflows"] });
    } catch (error) {
      toast.error(t("approvalWorkflows.statusError"));
    }
  };

  const handleSetDefault = async (workflowId) => {
    try {
      await api.post(`v1/approval-workflows/${workflowId}/set-default`);
      toast.success(t("approvalWorkflows.setDefault"));
      queryClient.invalidateQueries({ queryKey: ["approval-workflows"] });
    } catch (error) {
      toast.error(t("approvalWorkflows.setDefaultError"));
    }
  };

  const handleDeleteWorkflow = async () => {
    try {
      await api.delete(`v1/approval-workflows/${deletingWorkflowId}`);
      toast.success(t("approvalWorkflows.deleted"));
      setIsDeleteDialogOpen(false);
      setDeletingWorkflowId(null);
      queryClient.invalidateQueries({ queryKey: ["approval-workflows"] });
    } catch (error) {
      toast.error(t("approvalWorkflows.deleteError"));
    }
  };

  const openDeleteDialog = (workflowId) => {
    setDeletingWorkflowId(workflowId);
    setIsDeleteDialogOpen(true);
  };

  const filteredWorkflows = workflows.filter((workflow) => {
    const matchesSearch =
      workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workflow.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter =
      filterActive === "all" ||
      (filterActive === "active" && workflow.is_active) ||
      (filterActive === "inactive" && !workflow.is_active);
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("approvalWorkflows.title")}</h1>
          <p className="text-muted-foreground">
            {t("approvalWorkflows.subtitle")}
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="mr-2 h-4 w-4" />
          {t("approvalWorkflows.new")}
        </Button>
      </div>

      <WorkflowFiltersCard
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterActive={filterActive}
        setFilterActive={setFilterActive}
      />

      <WorkflowsListCard
        filteredWorkflows={filteredWorkflows}
        loading={loading}
        handleOpenDialog={handleOpenDialog}
        handleToggleStatus={handleToggleStatus}
        handleSetDefault={handleSetDefault}
        openDeleteDialog={openDeleteDialog}
      />

      <WorkflowFormDialog
        isDialogOpen={isDialogOpen}
        handleCloseDialog={handleCloseDialog}
        editingWorkflow={editingWorkflow}
        formData={formData}
        handleInputChange={handleInputChange}
        stepFormData={stepFormData}
        handleStepInputChange={handleStepInputChange}
        editingStepIndex={editingStepIndex}
        handleAddStep={handleAddStep}
        handleUpdateStep={handleUpdateStep}
        handleDeleteStep={handleDeleteStep}
        handleMoveStep={handleMoveStep}
        handleSubmit={handleSubmit}
        handleCancelStepEdit={handleCancelStepEdit}
      />

      <WorkflowDeleteDialog
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
        setDeletingWorkflowId={setDeletingWorkflowId}
        handleDeleteWorkflow={handleDeleteWorkflow}
      />
    </div>
  );
};

export default ApprovalWorkflows;
